import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X } from "lucide-react";
import StarRating from "@/components/StarRating";
import { submitModuleFeedback } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Post-test feedback prompt — rating + optional suggestion, shown after a
 * module test attempt (pass or fail). Skippable, never blocks navigation.
 */
export default function ModuleFeedbackPrompt({ moduleCode, attemptId }) {
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (dismissed) return null;

  if (submitted) {
    return (
      <div className="mt-4 rounded-2xl bg-[#ECFDF5] border border-[#A7F3D0] px-4 py-3 flex items-center gap-2 text-sm text-[#064E3B]" data-testid="module-feedback-thanks">
        <CheckCircle2 className="w-4 h-4" /> {t("module_feedback.thanks")}
      </div>
    );
  }

  const submit = async () => {
    if (!rating || busy) return;
    setBusy(true);
    try {
      await submitModuleFeedback(moduleCode, { rating, comment: comment.trim() || undefined, attempt_id: attemptId || undefined });
      setSubmitted(true);
    } catch {
      toast.error(t("module_feedback.toast_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl bg-white border border-[#E7E0D2] p-4" data-testid="module-feedback-prompt">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-[#1F2937]">{t("module_feedback.how_was_test")}</div>
        <button onClick={() => setDismissed(true)} data-testid="module-feedback-dismiss" className="p-1 rounded-full hover:bg-[#F9F5EE] text-[#9CA3AF]" title={t("module_feedback.dismiss")}>
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-2">
        <StarRating value={rating} onChange={setRating} testIdPrefix="module-feedback-star" size="w-8 h-8" />
      </div>
      <textarea data-testid="module-feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)}
        rows={2} maxLength={500} placeholder={t("module_feedback.suggestions_placeholder")}
        className="mt-2 w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none" />
      <button data-testid="module-feedback-submit" onClick={submit} disabled={!rating || busy}
        className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 text-white text-sm font-medium transition">
        {t("module_feedback.submit")}
      </button>
    </div>
  );
}
